import { fraxlendConfigFor } from '@1delta/data-sdk'
import { FraxlendLeverAbi, FraxlendPairAbi } from '@1delta/abis'
import { multicallRetryUniversal } from '@1delta/providers'
import { erc20Abi } from 'viem'
import type { FraxlendPairRaw, FraxlendPairsRaw } from './types'

/**
 * Fetch every curated Fraxlend pair on a chain — FULLY ON-CHAIN.
 *
 * There is NO Fraxlend API and no subgraph. There is also no on-chain
 * discovery, deliberately: the roster is the curated allowlist from
 * `data-sdk`, because both mainnet "registries" are traps (the deployer mixes
 * in 12 FraxlendV1 contracts and dozens of dust pairs; the other registry is
 * 62 Peapods pod-token pairs, several named `*TEST*`). If the allowlist is
 * empty we publish nothing — we never fall back to enumerating a deployer.
 *
 * Three rounds, each a single batched multicall:
 *   1. per-pair statics + live accounting (via `previewAddInterest`, so the
 *      numbers are post-accrual rather than stale-until-someone-pokes-it), and
 *      the LIVE `swappers()` leverage probe;
 *   2. ERC-20 metadata for both legs;
 *   3. nothing — everything else is derived.
 *
 * `allowFailure: true` throughout, and a pair is DROPPED if any of its
 * required reads failed rather than published with zeros. That is not
 * defensive boilerplate: FraxlendV1 pairs revert on `depositLimit`, so a bad
 * allowlist entry would otherwise publish a market with a 0 cap — i.e. a
 * silently frozen market — instead of being visibly absent.
 */

/** Reads per pair in round 1 — keep in lock-step with the slicer below. */
const PAIR_READS = 15

const big = (v: any): bigint => {
  if (typeof v === 'bigint') return v
  if (typeof v === 'number') return BigInt(v)
  if (typeof v === 'string' && v !== '0x') {
    try {
      return BigInt(v)
    } catch {}
  }
  return 0n
}

const bool = (v: any): boolean => v === true

const str = (v: any, fallback: string): string =>
  typeof v === 'string' && v.length > 0 ? v : fallback

const dec = (v: any, fallback = 18): number => {
  const n = Number(v)
  return Number.isFinite(n) && n >= 0 && n <= 36 ? n : fallback
}

export async function fetchFraxlendPairs(
  lender: string,
  chainId: string,
): Promise<FraxlendPairsRaw> {
  const config = fraxlendConfigFor(lender, chainId)
  const roster = (config?.pairs ?? []).filter((p) => !!p?.address)
  if (roster.length === 0) return { lender, config, pairs: [] }

  const swapper = config?.leverageSwapper ?? undefined
  const addresses = roster.map((p) => p.address)

  // --- round 1: statics + live accounting + the leverage probe ---
  const calls = addresses.flatMap((pair) => [
    { address: pair, name: 'symbol', params: [] },
    { address: pair, name: 'decimals', params: [] },
    { address: pair, name: 'asset', params: [] },
    { address: pair, name: 'collateralContract', params: [] },
    { address: pair, name: 'maxLTV', params: [] },
    { address: pair, name: 'LTV_PRECISION', params: [] },
    { address: pair, name: 'EXCHANGE_PRECISION', params: [] },
    { address: pair, name: 'LIQ_PRECISION', params: [] },
    { address: pair, name: 'depositLimit', params: [] },
    { address: pair, name: 'borrowLimit', params: [] },
    // Post-accrual accounting AND post-accrual rate state in one call. This is
    // the read the whole converter is built on — `getPairAccounting` and
    // `currentRateInfo` are their stale counterparts.
    { address: pair, name: 'previewAddInterest', params: [] },
    { address: pair, name: 'totalCollateral', params: [] },
    { address: pair, name: 'exchangeRateInfo', params: [] },
    { address: pair, name: 'rateContract', params: [] },
    // THE leverage gate. Probed live, per pair, every refresh — the mapping is
    // owner-mutable both ways, so a cached answer eventually lies in whichever
    // direction hurts (advertising a loop that reverts `BadSwapper()`, or
    // hiding one that works).
    {
      address: pair,
      name: 'swappers',
      params: [swapper ?? '0x0000000000000000000000000000000000000000'],
    },
  ])

  const res = (await multicallRetryUniversal({
    chain: chainId,
    calls,
    abi: calls.map(() => FraxlendPairAbi) as any,
    allowFailure: true,
  })) as any[]

  // Pause flags are a second batch purely to keep round 1's slicer readable;
  // they are cheap and never the reason a pair is dropped.
  const pauseCalls = addresses.flatMap((pair) => [
    { address: pair, name: 'isRepayPaused', params: [] },
    { address: pair, name: 'isWithdrawPaused', params: [] },
    { address: pair, name: 'isLiquidatePaused', params: [] },
    { address: pair, name: 'isInterestPaused', params: [] },
  ])
  // …plus, once for the whole roster, the SWAPPER's own kill switch. It is not
  // a per-pair fact and it is invisible from the pair: while `shutoff` is true
  // every `leveragedPosition` reverts `ShutOff()` even though `swappers()`
  // keeps reading true. Publishing `leverageEnabled: true` through that would
  // advertise a loop that cannot execute on ANY pair.
  if (swapper) {
    pauseCalls.push({ address: swapper, name: 'shutoff', params: [] })
  }
  const pauseRes = (await multicallRetryUniversal({
    chain: chainId,
    calls: pauseCalls,
    abi: pauseCalls.map((c) =>
      c.name === 'shutoff' ? FraxlendLeverAbi : FraxlendPairAbi,
    ) as any,
    allowFailure: true,
  }).catch(() => [])) as any[]
  // Trailing slot; a failed read must NOT read as "shut off" (that would hide
  // a working loop on every pair over a transient RPC failure).
  const swapperShutoff = swapper
    ? bool(pauseRes?.[pauseCalls.length - 1])
    : false

  // --- assemble, dropping any pair with an incomplete read ---
  type Partial = Omit<
    FraxlendPairRaw,
    | 'assetSymbol'
    | 'assetName'
    | 'assetDecimals'
    | 'collateralSymbol'
    | 'collateralName'
    | 'collateralDecimals'
  >
  const partials: Partial[] = []

  addresses.forEach((pair, i) => {
    const s = res.slice(i * PAIR_READS, i * PAIR_READS + PAIR_READS)
    const [
      symbol,
      decimals,
      asset,
      collateral,
      maxLtv,
      ltvPrec,
      exPrec,
      liqPrec,
      depositLimit,
      borrowLimit,
      preview,
      totalCollateral,
      xrInfo,
      rateContract,
      swapperOk,
    ] = s

    // A missing asset/collateral/preview means this is not a live v3.1.0 pair
    // (v1 contracts land here if the allowlist is wrong). Drop it loudly-by-
    // absence rather than publishing a zeroed market.
    if (typeof asset !== 'string' || typeof collateral !== 'string') return
    if (!preview || !xrInfo) return

    // `previewAddInterest` -> (interestEarned, feesAmount, feesShare,
    //                          rateInfo{5}, totalAsset{2}, totalBorrow{2})
    const rateInfo = (preview as any)[3] ?? {}
    const totalAsset = (preview as any)[4] ?? {}
    const totalBorrow = (preview as any)[5] ?? {}

    const p = pauseRes.slice(i * 4, i * 4 + 4)

    partials.push({
      pair,
      symbol: str(symbol, 'FraxlendPair'),
      decimals: dec(decimals),
      asset: asset.toLowerCase(),
      collateral: collateral.toLowerCase(),
      maxLtv: big(maxLtv),
      ltvPrecision: big(ltvPrec) || 100000n,
      exchangePrecision: big(exPrec) || 10n ** 18n,
      liqPrecision: big(liqPrec) || 100000n,
      cleanLiquidationFee: 0n,
      dirtyLiquidationFee: 0n,
      protocolLiquidationFee: 0n,
      depositLimit: big(depositLimit),
      borrowLimit: big(borrowLimit),
      totalAssetAmount: big(
        (totalAsset as any).amount ?? (totalAsset as any)[0],
      ),
      totalAssetShares: big(
        (totalAsset as any).shares ?? (totalAsset as any)[1],
      ),
      totalBorrowAmount: big(
        (totalBorrow as any).amount ?? (totalBorrow as any)[0],
      ),
      totalBorrowShares: big(
        (totalBorrow as any).shares ?? (totalBorrow as any)[1],
      ),
      totalCollateral: big(totalCollateral),
      oracle: str((xrInfo as any)[0], '').toLowerCase(),
      maxOracleDeviation: Number((xrInfo as any)[1] ?? 0),
      exchangeRateLastTimestamp: big((xrInfo as any)[2]),
      lowExchangeRate: big((xrInfo as any)[3]),
      highExchangeRate: big((xrInfo as any)[4]),
      ratePerSec: big((rateInfo as any).ratePerSec ?? (rateInfo as any)[3]),
      fullUtilizationRate: big(
        (rateInfo as any).fullUtilizationRate ?? (rateInfo as any)[4],
      ),
      feeToProtocolRate: Number(
        (rateInfo as any).feeToProtocolRate ?? (rateInfo as any)[1] ?? 0,
      ),
      rateLastTimestamp: big(
        (rateInfo as any).lastTimestamp ?? (rateInfo as any)[2],
      ),
      rateContract: str(rateContract, '').toLowerCase(),
      isRepayPaused: bool(p[0]),
      isWithdrawPaused: bool(p[1]),
      isLiquidatePaused: bool(p[2]),
      isInterestPaused: bool(p[3]),
      // Only meaningful when a swapper is configured; with none, the probe was
      // against the zero address and must never read as "leverage available".
      leverageSwapperApproved: !!swapper && bool(swapperOk) && !swapperShutoff,
      leverageSwapper: swapper,
    })
  })

  if (partials.length === 0) return { lender, config, pairs: [] }

  // --- round 2: ERC-20 metadata for both legs ---
  const tokenSet = [
    ...new Set(partials.flatMap((p) => [p.asset, p.collateral])),
  ]
  const metaCalls = tokenSet.flatMap((t) => [
    { address: t, name: 'symbol', params: [] },
    { address: t, name: 'name', params: [] },
    { address: t, name: 'decimals', params: [] },
  ])
  const metaRes = (await multicallRetryUniversal({
    chain: chainId,
    calls: metaCalls,
    abi: metaCalls.map(() => erc20Abi) as any,
    allowFailure: true,
  }).catch(() => [])) as any[]

  const meta = new Map<
    string,
    { symbol: string; name: string; decimals: number }
  >()
  tokenSet.forEach((t, i) => {
    const [symbol, name, decimals] = metaRes.slice(i * 3, i * 3 + 3)
    meta.set(t, {
      symbol: str(symbol, '?'),
      name: str(name, '?'),
      decimals: dec(decimals),
    })
  })

  const pairs: FraxlendPairRaw[] = partials.map((p) => {
    const a = meta.get(p.asset)
    const c = meta.get(p.collateral)
    return {
      ...p,
      assetSymbol: a?.symbol ?? '?',
      assetName: a?.name ?? '?',
      assetDecimals: a?.decimals ?? 18,
      collateralSymbol: c?.symbol ?? '?',
      collateralName: c?.name ?? '?',
      collateralDecimals: c?.decimals ?? 18,
    }
  })

  return { lender, config, pairs }
}
